import { toRaw, watch, ref, computed } from 'vue';
import { k as db, z as fkEl, v as logger, b as useConfigStore, a as useAuthStore, q as glob, t as registerPpShopEvent, G as registerLocalUploadEvent, h as ppapiClient, c as useAlertStore } from './prodsel.store-B-MQAyQE.mjs';
import { storeToRefs } from 'pinia';
import { n as printTypes, f as filterDef, h as mediaProductOptions, a as autoLayoutPgr, b as autoLayoutCat, l as layoutScheme, c as layoutSlideshow, g as mediaConditionFilter } from './pp-product-config-DylFCRxi.mjs';
import { u as updateMediaImageResolutions } from './imageResolution-BoTJnVec.mjs';
import { MerkleJson } from 'merkle-json';
import { _, isObject, isString } from 'lodash';
import { useOnline } from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';

function getSortValue(item, property) {
    return `${property || ''}`.split('.').reduce((value, key) => value?.[key], item)
}

function sortOptionEntries(entries, settings = {}) {
    const sort = settings.sort;
    if (!sort || !Array.isArray(entries)) return entries

    const toStart = Array.isArray(sort.toStart) ? sort.toStart : [];
    const toEnd = Array.isArray(sort.toEnd) ? sort.toEnd : [];
    const pinnedIndex = (entry, values) => values.findIndex((value) => value == entry.value || value == entry.label);
    const compareValues = (left, right) => {
        const leftNumber = Number(left);
        const rightNumber = Number(right);

        if (left !== '' && right !== '' && Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
            return leftNumber - rightNumber
        }

        return `${left ?? ''}`.localeCompare(`${right ?? ''}`, undefined, { numeric: true, sensitivity: 'base' })
    };

    return entries
        .map((entry, index) => ({ ...entry, index }))
        .sort((left, right) => {
            const leftStart = pinnedIndex(left, toStart);
            const rightStart = pinnedIndex(right, toStart);
            if (leftStart !== -1 || rightStart !== -1) {
                if (leftStart === -1) return 1
                if (rightStart === -1) return -1
                return leftStart - rightStart
            }

            const leftEnd = pinnedIndex(left, toEnd);
            const rightEnd = pinnedIndex(right, toEnd);
            if (leftEnd !== -1 || rightEnd !== -1) {
                if (leftEnd === -1) return -1
                if (rightEnd === -1) return 1
                return leftEnd - rightEnd
            }

            if (sort.by) {
                const compared = compareValues(left.sortValue, right.sortValue);
                if (compared) return sort.direction === 'desc' ? -compared : compared
            }

            return left.index - right.index
        })
}

function matchesProductParameters(product, requiredProduct) {
    if (!requiredProduct || typeof requiredProduct !== 'object') return true
    if (!product || typeof product !== 'object') return false

    return Object.entries(requiredProduct).every(([key, expected]) => {
        const actual = product[key];

        if (expected && typeof expected === 'object' && !Array.isArray(expected)) {
            return matchesProductParameters(actual, expected)
        }

        if (Array.isArray(expected)) {
            return Array.isArray(actual)
                && expected.length === actual.length
                && expected.every((value, index) => value == actual[index])
        }

        return actual == expected
    })
}

class ProductSelector {

    /**
     * Creates an instance of ProductSelector.
     * 
     * @constructor
     * @param {Object} layout - The layout configuration for the product selector.
     * 
     * @property {Object} layout - The layout configuration for the product selector.
     * @property {boolean} autoSelect - Flag to determine if auto-select is enabled.
     * @property {boolean} presetPid - Flag to determine if a preset product ID is used.
     * @property {boolean} showFinalProduct - Flag to determine if the final product should be shown.
     * @property {Object} optgroup - Object to store option groups.
     * @property {Object} status - Object to store the status of the form and final products.
     * @property {Object} status.form - Object to store the form status.
     * @property {Array} status.finalProducts - Array to store the final products.
     * @property {Array} currentFilter - The current filter applied to the products.
     * @property {Array} currentFilterList - List of products after applying the current filter.
     * @property {string} displayFinal - Mode to display the final product ('auto' by default).
     * @property {string} prgFilter - Filter for price groups (default is 'DE_1').
     * @property {MerkleJson} mj - Instance of MerkleJson for handling JSON operations.
     * @property {Function|null} prefilter - Function to prefilter products, takes currentFilter as argument.
     * @property {boolean} slideshow - Flag to determine if slideshow is enabled.
     * @property {Function} t - Translation function from useI18n.
     */
    constructor(layout) {
        this.layout = layout;
        this.autoSelect = true;
        this.stepByStep = false;
        this.presetPid = false;
        this.showFinalProduct = false;
        this.optgroup = {};
        this.pid = null;
        this._status = {
            form: {},
            finalProducts: [],
        };
        this.currentFilter = db.products;
        this.currentFilterList = [];
        this.displayFinal = 'auto';
        this.prgFilter = 'DE_1'; // TODO Preisgruppenfilter
        this.mj = new MerkleJson();
        //  window.currentFilterList = this.currentFilterList // DEBUG

        // Prefilter ist eine Funktion, die this.currentFilter als Argument nimmt
        // und mit welcher die Produkte vorgefiltert werden können
        this.prefilter = null;
        this.slideshow = false;
        this.onMediaChanged = null;
        // dummy translation function. Needs to be replaced by i18n
        this.t = function (str) { return str };

        Object.defineProperty(this, 'status', {
            configurable: true,
            enumerable: true,
            get: () => this._status,
            set: (nextStatus) => {
                this._status = nextStatus || { form: {}, finalProducts: [] };
                this.bindMediaStatus();
            }
        });

        this.status = this._status;
    }

    bindMediaStatus() {
        const status = this._status || {};
        const currentMedia = status.media;

        Object.defineProperty(status, 'media', {
            configurable: true,
            enumerable: true,
            get: () => this._mediaValue || [],
            set: (nextMedia) => {
                const previousMedia = Array.isArray(this._mediaValue) ? [...this._mediaValue] : [];
                const media = Array.isArray(nextMedia) ? nextMedia : (nextMedia ? [nextMedia] : []);

                if (this.mediaValuesEqual(previousMedia, media)) return

                this._mediaValue = media;
                this.applyMediaPresets();

                if (typeof this.onMediaChanged == "function") {
                    this.onMediaChanged(media, previousMedia);
                }
            }
        });

        if (typeof currentMedia !== 'undefined') {
            status.media = currentMedia;
        }
    }

    setMedia(media) {
        this.status.media = media;
        return this.status.media
    }

    mediaValuesEqual(firstMedia, secondMedia) {
        if (firstMedia === secondMedia) return true
        if (!Array.isArray(firstMedia) || !Array.isArray(secondMedia)) return false
        if (firstMedia.length !== secondMedia.length) return false

        return firstMedia.every((mediaItem, index) => mediaItem === secondMedia[index])
    }

    async applyMediaPresets() {
        const mediaItem = this.status?.media?.[0];
        const mediaOptions = mediaItem?.options;

        if (!mediaItem || !mediaOptions || typeof mediaOptions !== 'object') {
            return false
        }

        this.status?.product?.widthMM;
        const presets = Object.entries(mediaOptions);

        for (const [optionName, optionValue] of presets) {
            if (optionValue === undefined || optionValue === null || optionValue === '') continue

            const matches = await db.productOptions
                .where('name')
                .equals(optionName)
                .toArray();

            if (!matches[0]) continue

            matches[0];
            //const groupName = option.parameter?.group ? `po_${option.parameter.group}` : `po_${option.po}`
            this.status.optionPresets = this.status.optionPresets || {};
            this.status.optionPresets[optionName] = optionValue;
        }

        return true
    }

    isAutoSelect() {
        let auto = (this.autoSelect || this.presetPid) ? true : false;
        return auto
    }
    /*
    *  Wrapper-Funktion für alle DB Abfragen
    *
    *  Die DB Abfragen durchlaufen einen mehrstufigen Filter.
    *  Beginnend von db.products wird pro form-Element ein weiterer
    *  Filter angehängt. Die aktuelle Verkettung aller Filter ist
    *  immer in this.currentFilter gespeichert
    * 
    */

    getOpt(loScheme) {


        switch (loScheme.filter) {

            case "column": return this.genOptions(loScheme, loScheme.group);
            case "tag": return this.genTagOptions(loScheme, loScheme.group);
            case "cat": return this.genCatOptions(loScheme, loScheme.group);
            case "final": return this.genFinalOptions(loScheme, loScheme.group);

        }


    }

    async genFinalOptions(loScheme, filterId) {

        // Options-Array aus Finalen Items erstellen

        let options = {};

        let finalProd = await this.currentFilter.toArray();
        this.status.finalProducts = finalProd;

        let pidFilter = this.status.form["pid"];
        let pidAvailable = false;

        const labelFields = ['label', 'format', 'surface'];
        const differingFields = labelFields.filter((field) => {
            const values = new Set(finalProd.map((prod) => `${prod?.[field] ?? ''}`));
            return values.size > 1
        });

        let preselect = null;
        finalProd.forEach(prod => {
            if (!preselect) preselect = prod.pid;

            const optionLabel = differingFields
                .map((field) => `${prod?.[field] ?? ''}`.trim())
                .filter((value) => value !== '')
                .join(' ');

            options[prod.pid] = optionLabel || prod.pid;
            if (pidFilter == prod.pid) pidAvailable = true;
        });

        // Nur bei autoSelect default eintrag (1. Element) setzen
        if (!pidAvailable && this.isAutoSelect() && (!this.status.form["pid"] || !options[this.status.form["pid"]])) {
            this.status.form["pid"] = preselect;
        }
        

        // Filter hinzufügen wenn pidFilter in ergebnisliste

        if (pidAvailable) {
            this.currentFilter = this.currentFilter.and(item => item.pid == pidFilter);
            let cf = { pid: pidFilter };
            this.currentFilterList.push(cf);
        } else if (finalProd.length > 0) {
            if (finalProd.length == 1) this.currentFilter = this.currentFilter.and(item => item.pid == preselect);
            let cf = { pid: preselect };
             this.currentFilterList.push(cf);
        }
        
        // sollte pid bereits null gesetzt worden sein, hier definitiv löschen
        // wichtig für bcView, damit Filterreihenfolge stimmt
        if (this.status.form?.pid == null) {
             delete this.status.form.pid;
        }

        return options

    }


    /*
    * Filtern nach Tabellenspalte
    * Beschreibungen werden aus pp-product-config parameter ausgelesen.
    * Die Datenbankabfrage ermittelt alle verfügbaren Produkte des Filters 
    */
    async genOptions(loScheme, filterId) {

        let column = loScheme.group;
        let name = loScheme.group;
        let def = false;
        let tKey = false; // Translation key

        // Zum Gruppieren temp array schreiben

        if (!db.temp) db.temp = {};
        db.temp[name] = {};
        const optionSortValues = {};
        const optionPrintTypes = {};
        const showPrintType = !!loScheme.settings?.showPrintType && printTypes;

        // Falls Filterdefinition vorhanden 
        if (filterDef[name]) {  
            // Definition ist eine Übersetzungsanweisung 
            if (filterDef[name]["_translate_"]) {
                tKey = filterDef[name]["_translate_"];
                def = {};
            // ansonsten Definition direkt übernehmen    
            } else {
                def = { ...filterDef[name] };
            }
        } 


        await this.currentFilter.each((item) => {
 
            let itemValue = item[column];
            if (loScheme.settings?.sort?.by && typeof optionSortValues[itemValue] === 'undefined') {
                optionSortValues[itemValue] = getSortValue(item, loScheme.settings.sort.by);
            }
            if (showPrintType && Array.isArray(item.tags)) {
                if (!optionPrintTypes[itemValue]) optionPrintTypes[itemValue] = new Set();
                Object.keys(printTypes).forEach((key) => {
                    if (item.tags.includes(printTypes[key].tag)) optionPrintTypes[itemValue].add(key);
                });
            }
            if (tKey) {
                def[itemValue] = { label: this.t(tKey, itemValue) };
            }

            if (def) {
                let gk = Object.keys(def);
                if (gk.includes(item[column].toString())) {
                    if (!db.temp[name][itemValue]) db.temp[name][itemValue] = 1;
                    else db.temp[name][itemValue]++;
                }
            } else {
                // ansonsten Filterdefinition aus Spaltenname erzeugen
                if (!db.temp[name][itemValue]) {
                    db.temp[name][itemValue] = 1;
                } else {
                    db.temp[name][itemValue]++;
                }
            }
        });

        // Wenn Filterdefinition vorhanden, Reihenfolge übernehmen
        if (def) {
            let sort = {};
            for (let n in def) {
                if (db.temp[name][n]) sort[n] = db.temp[name][n];
            }
            db.temp[name] = sort;
        }


        // Options-Array aus verfügbaren Items erstellen

        let options = {};
        let preselect = false;

        const optionEntries = Object.keys(db.temp[name]).map((row) => {
            let label = def && def[row]?.label ? def[row].label : row;
            if (optionPrintTypes[row]?.size) {
                const printTypeLabels = Object.keys(printTypes)
                    .filter((key) => optionPrintTypes[row].has(key))
                    .map((key) => printTypes[key].label);
                label += ` - ${printTypeLabels.join(', ')}`;
            }
            return { value: row, label, sortValue: optionSortValues[row] }
        });

        for (const entry of sortOptionEntries(optionEntries, loScheme.settings)) {
            const row = entry.value;
            if (!preselect) preselect = row;
            if (row && row != "undefined") options[row] = entry.label;
        }

        // Nur bei autoSelect: Falls Option nicht gewählt, oder vormals gewählte nicht verfügbar default eintrag (1. Element) setzen
        if (this.isAutoSelect() && (!this.status.form[filterId] || !options[this.status.form[filterId]])) {
            this.status.form[filterId] = preselect;
        }

        // Wenn nur eine Option vorhanden, diese automatisch wählen
        // Das ist notwendig, da Ein-Option-Selektoren ausgeblendet werden. im Fall von bcView ist sonst die Fortsetzung blockiert
        if (Object.keys(options).length == 1) {
            this.status.form[filterId] = preselect;
        }

        // Filter nur hinzufügen wenn Optionen vorhanden

        if (Object.keys(options).length == 0) return false

        let filter = this.status.form[filterId];
        this.currentFilter = this.currentFilter.and(item => item[column] == filter);
        let cf = {};
        cf[column] = filter;
        this.currentFilterList.push(cf);

        // Falls vorgherige Auswahl nicht in aktueller Optionsliste vorhanden => Auswahl löschen
        if (!options[this.status.form[filterId]]) delete this.status.form[filterId];

        return options

    }

    /*
    * Filtern nach Tags (Tabellenspalte "tags")
    * Beschreibungen werden aus pp-product-config parameter ausgelesen.
    * Die Datenbankabfrage ermittelt alle verfügbaren Produkte des Filters
    */
    async genTagOptions(loScheme, filterId) {

        let name = loScheme.group;

        if (!filterDef[name]) return {}

        let def = filterDef[name];

        // Die each() funktion von Dexie hat keinen scope parameter
        // Daher muss eine art "globale" Variable erstellt werden
        // Dazu wird ein Temp Objekt in db angelegt
        if (!db.temp) db.temp = {};
        db.temp[name] = {};

        await this.currentFilter.each((item) => {

            let rk = Object.keys(def);
            for (let i in item.tags) {

                if (rk.includes(item.tags[i])) {
                    if (!db.temp[name][item.tags[i]]) db.temp[name][item.tags[i]] = 1;
                    else db.temp[name][item.tags[i]]++;

                }
            }
        });

        // Options-Array aus verfügbaren Items erstellen

        let options = {};
        let preselect = false;
        for (let row in db.temp[name]) {
            if (!preselect) preselect = row;
            if (def[row]) options[row] = def[row].label;
        }

        // Falls Option nicht gewählt, default eintrag (1. Element) setzen
        if (this.isAutoSelect() && (!this.status.form[filterId] || !options[this.status.form[filterId]])) {
            this.status.form[filterId] = preselect;
        }

        // Wenn nur eine Option vorhanden, diese automatisch wählen
        if (Object.keys(options).length == 1) {
            this.status.form[filterId] = preselect;
        }
    
        // Filter nur hinzufügen wenn Optionen vorhanden

        if (Object.keys(options).length == 0) return false
       
        let filter = this.status.form[filterId];
        this.currentFilter = this.currentFilter.and(item => item.tags.includes(filter));
        let cf = {};
        cf[name] = filter;
        this.currentFilterList.push(cf);


        // Falls vorgherige Auswahl nicht in aktueller Optionsliste vorhanden => Auswahl löschen
        if (!options[this.status.form[filterId]]) delete this.status.form[filterId];

        return options

    }

    /*
    * Filtern nach Kategorie (Tabellenspalte "cat")
    * Beschreibungen werden aus pp-product-config parameter ausgelesen.
    * Die Datenbankabfrage ermittelt alle verfügbaren Produkte des Filters
    */
    async genCatOptions(loScheme, filterId) {

        let name = loScheme.group;

        if (!filterDef[name]) return {}

        let def = filterDef[name];

        // Die each() funktion von Dexie hat keinen scope parameter
        // Daher muss eine art "globale" Variable erstellt werden
        // Dazu wird ein Temp Objekt in db angelegt
        if (!db.temp) db.temp = {};
        db.temp[name] = {};

        await this.currentFilter.each((item) => {

            let rk = Object.keys(def);
            for (let i in item.cat) {

                if (rk.includes(item.cat[i])) {
                    if (!db.temp[name][item.cat[i]]) db.temp[name][item.cat[i]] = 1;
                    else db.temp[name][item.cat[i]]++;

                }
            }
        });

        // Options-Array aus verfügbaren Items erstellen

        let options = {};
        let preselect = false;
        for (let row in db.temp[name]) {
            if (!preselect) preselect = row;
            if (def[row]) options[row] = def[row].label;
        }

        // Falls Option nicht gewählt, default eintrag (1. Element) setzen
        if (this.isAutoSelect() && (!this.status.form[filterId] || !options[this.status.form[filterId]])) {
            this.status.form[filterId] = preselect;
        }

        // Wenn nur eine Option vorhanden, diese automatisch wählen
        if (Object.keys(options).length == 1) {
            this.status.form[filterId] = preselect;
        }

        // Filter nur hinzufügen wenn Optionen vorhanden

        if (Object.keys(options).length == 0) return false

        let filter = this.status.form[filterId];
        this.currentFilter = this.currentFilter.and(item => item.cat.includes(filter));
        let cf = {};
        cf[name] = filter;
        this.currentFilterList.push(cf);

        // Falls vorgherige Auswahl nicht in aktueller Optionsliste vorhanden => Auswahl löschen
        if (!options[this.status.form[filterId]]) delete this.status.form[filterId];

        return options

    }

    setPrefilter() {
        
        this.status.fileFilter = false;

        if (!this.status?.form['preFilter']) return false

        let activeFilters = _.cloneDeep(this.status.form['preFilter'].filter((item) => item.active));

        let orgroups = {};

        activeFilters.forEach((item) => {
            // Filtergruppen mit ODER-Verknüpfung sammeln
            // Bei mehrern Filtern mit gleichem Namen muss values definiert sein
            // diese Arrays werden kombiniert um mit einer Abfrage auf alle Werte testen zu können
            if (!orgroups[item.filter]) orgroups[item.filter] = item;
            else orgroups[item.filter].values.push(...item.values);
            
        });

        Object.keys(orgroups).forEach((group) => {
            this.applyPrefilterItem(orgroups[group]);
        });

    }

    applyPrefilterItem(item) {

        // Mögliche Filter für preFilterDef in pp-product-config

        let pgr = "";
        switch (item.filter) {
            // Passende Datei mittels mediaType match
            case "filematch" :
                // nur anwenden wenn Medientyp gesetzt ist
                if (this.status.media && this.status.media[0]?.type) {
                  const mediaType = this.status.media[0].type;
                  const mediaPages = Number(this.status.media[0].pages);

                  if (mediaType == "application/pdf") {
                    this.currentFilter = this.currentFilter.and(el => {
                      const width = Number(el.widthMM ?? 0) + Number(el.trimAbs ?? 0) * 2;
                      const height = Number(el.heightMM ?? 0) + Number(el.trimAbs ?? 0) * 2;
                      const mediaWidth = Number(this.status.media[0].width ?? 0);
                      const mediaHeight = Number(this.status.media[0].height ?? 0);

                      return width === mediaWidth && height === mediaHeight
                    });

                    
                  } else {
                    this.currentFilter = this.currentFilter.and(el => el.mediaType == mediaType);
                  }

                  this.currentFilter = this.currentFilter.and(el => {
                      const minPages = Number(el.pageMin ?? 0);
                      const maxPages = Number(el.pageMax ?? Number.MAX_SAFE_INTEGER);

                      return Number.isFinite(mediaPages) && mediaPages >= minPages && mediaPages <= maxPages
                  });

                  this.status.fileFilter = {
                    type: mediaType,
                    list: this.currentFilter.toArray()
                  };
                }
            break;
            // Druckart über produktgruppe testen 
            case "printtype" :
                // TODO dynamische pgrs
                pgr = {9:"offset",10:"digital"};
                if (Array.isArray(item.values)) {
                   this.currentFilter = this.currentFilter.and(el => item.values.indexOf(pgr[el.pgrId]) > -1 );
                }
            break;
        }

    }

    /**
     * Asynchronously retrieves the product config, including options and addons. Registers also product option prices in status.
     * 
     * @returns {Promise<Object>} A promise that resolves to an object containing the product config with options and addons.
     * 
     * @example
     * const productConfig = await getProductConfig();
     * console.log(productConfig.options);
     * console.log(productConfig.addons);
     */
    async getProductConfig() {

        const config = {options:[],addons:[]};
        const that = this;

        // Produktsetup Optionen
        const  makeList = async function (options) {

            const list = [];

            for (let i in options) {
                // Optionsname aus der Datenbank
                let optGroup = {name:""};
                [optGroup] = await db.productOptions.where("po").equals(parseInt(i)).toArray(); 

                // check parameters for the current option group
                let parameter = optGroup.parameter ||  {};
                if (parameter.media ) {
                    // Wenn die Option zusätzliche Seiten erfordert, aber keine zusätzlichen Seiten vorhanden sind, überspringen.
                    if (parameter.media.includes("hasExtraPages")) {
                        if ( extraPagesCount == 0) continue
                    }
                }
                if (!matchesProductParameters(that.status.product, parameter.product)) continue

                let opt = await db.productOptions.where("po").anyOf(options[i]).toArray();
                opt = opt.filter((option) => matchesProductParameters(that.status.product, option.parameter?.product));
                if (opt.length === 0) continue
                let optPairs = {};
                let optObjects = [];
                for (let n in opt) {
                    let label = "";
                    // Spezialfall PO = 0 für nicht gesetzte Option
                    // Bekommt groupname + not_set
                    if (opt[n].po == 0) {
                       label = optGroup.name + " not_set";
                    } else {
                        label = opt[n].name;
                    }
                    if (typeof opt[n].value == "string" && opt[n].value !== "" ) {
                        label += " " + opt[n].value;
                    }
                    optPairs[opt[n].po] = that.t("product." + label);
                    optObjects.push({po:opt[n].po, name:opt[n].name, value: opt[n].value, pid:opt[n].pid, groupId: i});
                    
                    // Falls preset vorhanden, nur einmalig beim Medienwechsel anwenden.
                    // Eine bereits vom Nutzer gesetzte Auswahl darf nicht sofort wieder durch das Preset überschrieben werden.
                    const formKey = "po_" + optGroup.po;
                    const presetValue = that.status.optionPresets?.[optGroup.name];
                    const currentValue = that.status.form?.[formKey];

                    if (typeof presetValue !== "undefined" && typeof currentValue === "undefined") {
                        if (presetValue == opt[n].value) {
                            that.status.form[formKey] = opt[n].po;
                        }
                    }

                    // falls pid vorhanden, preis in optionPrices speichern
                    // einmal mit po und einmal mit name als key
                    if (opt[n].pid) {
                        let opPrice = {pid: opt[n].pid, groupId: optGroup.po, groupName: "po_" + optGroup.po, name: opt[n].name};
                        that.status.optionPrices[opt[n].po] = opPrice;
                        //that.status.optionPrices[opt[n].name] = opPrice
                    }
                }

                that.status.productOptions.push(optObjects);

                list.push({
                    name: "po_" + optGroup.po,
                    label: that.t("product." + optGroup.name),
                    options: optPairs
                });
            }

            return list

        };

         // Seitenoptionen
        let extraPagesCount = 0;
        if (this.status.media && this.status.media[0]?.pages > this.status.product.pageMin) {
            extraPagesCount = this.status.media[0].pages - this.status.product.pageMin;
        }

        if (this.status.product?.setup?.config?.options) config["options"] = await makeList(this.status.product.setup.config.options);
        if (this.status.product?.setup?.config?.addons) config["addons"] = await makeList(this.status.product.setup.config.addons);

        if (!this.status.media || !this.status.media[0]?.options) return config

        let mediaOptions = this.status.media[0].options;
        // Optionen aus Medienobjekt
        for (let i in mediaOptions) {
            if (mediaProductOptions[i]) {
                let opt = await db.productOptions.where("name").equals(i).and(item => (
                    item.value == mediaOptions[i]
                    && matchesProductParameters(this.status.product, item.parameter?.product)
                )).toArray();
                if (opt[0]) {
                    let optPair = {};
                    let po = opt[0].po;
                    let groupName = "po_" + po;
                    if (opt[0].parameter?.group) groupName = "po_" + opt[0].parameter.group;
                    optPair[po] = this.t("product." + i);

                    this.status.productOptions.push({po: opt[0].po, name: i, value: mediaOptions[i], groupName: groupName, pid: opt[0].pid});

                    
                    // falls pid vorhanden, preis in optionPrices speichern

                    if (opt[0].pid) {
                        //this.status.optionPrices[opt[0].po] = {pid: opt[0].pid, value: mediaOptions[i], groupName:  "po_" + i, name: i}
                        this.status.optionPrices[i] = {pid: opt[0].pid, value: mediaOptions[i], groupName: groupName, name: i};
                    }

                    config.options.push({
                        name: groupName,
                        label: this.t("product." + i),
                        options: optPair,
                        preselect:1
                    });

                }
            }
        }

        return config


    }


    /**
     * Generates a form with options based on the provided product configuration.
     *
     * @param {Object} productConfig - The configuration object for the product.
     * @param {Array} productConfig.options - An array of option configurations.
     * @param {string} productConfig.options[].name - The name of the option.
     * @param {string} productConfig.options[].label - The label of the option.
     * @param {Array} productConfig.options[].options - The available options for the select element.
     * @returns {Array} An array of formkit element objects representing the form options.
     */
    genOptionsForm(productConfig) {

        let fksOpt = [];
        let optionsSetCount = 0;

        for (let i in productConfig.options) {

            let config = productConfig.options[i];

            let formType = 'select';
            if (config.fixed) {
                formType = 'radio';
                this.setForm(config.name, Object.keys(config.options));
            } else {
                this.clearForm(config.name, Object.keys(config.options));
            }
            
            if (config.preselect) {
                formType = 'checkbox';
                // Bei preselect Option nur erstmalig aktivieren oder bei Produktwechsel
                if (typeof this.status.form[config.name] == "undefined" || this.status.product.pid != this.pid) this.setForm(config.name, Object.keys(config.options));
            } 
            
            let sel = new fkEl(formType, "", config.name, config.label, config.options, this.t);
            if (config.fixed) sel.setHelp("Bearbeiten Sie Ihren Kalender um diese Option zu ändern");
            sel.key = this.mj.hash(sel);
            sel.selected = this.status.form[config.name];

            fksOpt.push(sel);

            // Falls es sich um ein Checkbox oder Radio handelt, wird ein Bestätigungs-Element hinzugefügt
            if (this.stepByStep && (formType == 'checkbox' || formType == 'radio')) {
                if (!this.status.form["confirm_" + config.name]) this.setForm("confirm_" + config.name,  false);
            }

            if (!sel.selected || this.status.form["confirm_" + config.name] === false) {
                this.status.optionsSet = false;
                if (this.stepByStep)  {
                    if (this.status.form["confirm_" + config.name] === false) {
                        sel.addConfirm();
                    }
                    return fksOpt
                }
            } else {
                optionsSetCount++;
            }

            
        }

        for (let i in productConfig.addons) {

            let config = productConfig.addons[i];
            let formType = 'select';

            // Bei fixed Option immer aktivieren
            if (config.fixed) {
                formType = 'radio';
                this.setForm(config.name, Object.keys(config.options));
            } else {
                this.clearForm(config.name, Object.keys(config.options));
            }
            
            // Bei preselect Option nur erstmalig aktivieren
            if (config.preselect) {
                formType = 'checkbox';
                if (typeof this.status.form[config.name] == "undefined") this.setForm(config.name, Object.keys(config.options));
            } 
            
            if (Object.keys(config.options).length == 1) formType = 'checkbox';
            let sel = new fkEl(formType, "", config.name, config.label, config.options, this.t);
            if (config.fixed) sel.setHelp("Bearbeiten Sie Ihren Kalender um diese Option zu ändern");
            sel.key = this.mj.hash(sel);
            sel.selected = this.status.form[config.name];

            fksOpt.push(sel);

            // Falls es sich um ein Checkbox oder Radio handelt, wird ein Bestätigungs-Element hinzugefügt
            if (this.stepByStep && (formType == 'checkbox' || formType == 'radio')) {
                if (!this.status.form["confirm_" + config.name]) this.setForm("confirm_" + config.name, false);
            }

            if (!sel.selected && !this.status.form["confirm_" + config.name]) {
                this.status.optionsSet = false;
                if (this.stepByStep) {
                    if (this.status.form["confirm_" + config.name] === false) {
                        sel.addConfirm();
                    }
                    break
                }
            } else {
                optionsSetCount++;
            }
        }

        if (optionsSetCount == productConfig.options.length + productConfig.addons.length) this.status.optionsSet = true;

        this.pid = this.status.product.pid;
        return fksOpt

    }

    setForm(key, value) {
        this.status.form[key] = value;
    }

    clearForm(key, value) {

        if (!Array.isArray(value)) {
             if (key != value) this.status.form[key] = null;
        } else if (Array.isArray(this.status.form[key])) {
            this.status.form[key].forEach((val) => {
                if (!value.includes(val)) {
                    let idx = this.status.form[key].indexOf(val);
                    if (idx > -1) {
                        this.status.form[key].splice(idx, 1);
                    }
                }
           });
        }

    }


    /**
     * Retrieves the FormKit schema for a given product ID. It uses layoutScheme from pp-product-config.js
     * The single Formkit elements are instances from fkEl class
     * 
     * @param {string} pid - The product ID to filter the schema list for a specific product.
     * @returns {Promise<Array|Object>} - Returns an array of schema elements or an object containing the schema elements wrapped in a slideshow container.
     * 
     * @async
     */
    async getFkSchema(pid) {

        let fks = [];
        let prgFilter = this.prgFilter;
        this.currentFilter = db.products.where("state").equals("verfügbar").and(item => item.prices.findIndex(function (el) { return el.priceGroup == prgFilter }) > -1).and(item => ['VP', 'AP'].indexOf(item.pid.substring(0, 2)) < 0);
        // mit dem pid-Parameter kann die Filter-Liste für ein bestimmtes Produkt ermittelt werden
        if (pid) {
            this.currentFilter = this.currentFilter.and(item => item.pid == pid);
            this.presetPid = pid;
        } else {
            this.presetPid = false;
            this.setPrefilter();
        }

        if (typeof this.preFilter == "function") {
            logger.debug("applying prefilter"); 
           // this.currentFilter = this.preFilter(this.currentFilter)
        } 

        this.currentFilterList = [];
        this.optgroup = {};

        let currentLayout = false;

        if (this.layout == 'auto') {
            if (this.status.form['pgrId']) {
                let pgrId = this.status.form['pgrId'];
                currentLayout = autoLayoutPgr[pgrId];
            }
            if (this.status.form['cat']) {
                let cat = this.status.form['cat'];
                currentLayout = autoLayoutCat[cat];
            }
        } else {
            currentLayout = this.layout;
        }

        if (!currentLayout) currentLayout = 'standard';

        this.currentLayout = currentLayout;

        for (var i in layoutScheme[currentLayout]) {

            var loScheme = layoutScheme[currentLayout][i];
            if (loScheme.form != 1) {
                if (loScheme.schema) fks.push(loScheme.schema);
                continue
            }

            let filterId = ""; // Durch hash erseztzt
            var sel = new fkEl(loScheme.type, filterId, loScheme.group, loScheme.label, undefined, this.t, loScheme.settings);
            if (!this.optgroup[loScheme.group]) {
                logger.debug("generate options for " + loScheme.group);
                this.optgroup[loScheme.group] = await this.getOpt(loScheme);
            }

            if (this.optgroup[loScheme.group]) {
                if (loScheme.filter == 'final' && loScheme.type == 'radio') {
                    sel.setOptions(Object.entries(this.optgroup[loScheme.group]).map(([pid, label]) => ({
                        value: pid,
                        label,
                        help: pid
                    })));
                    sel.children.find((child) => child.$formkit).sectionsSchema = {
                        optionHelp: {
                            children: [{
                                $cmp: 'ProductInfoButton',
                                props: {
                                    pid: '$option.value'
                                }
                            }]
                        }
                    };
                } else {
                    sel.setOptions(this.optgroup[loScheme.group]);
                }
            }

            let optLen = (sel.getOptions())?Object.keys(sel.getOptions()).length:0;

            // Bei display=auto Filter ausblenden, die nur ein Element haben  
            if (loScheme.display=="auto" && optLen  < 2) continue

            // als key wird der hash des schemas ohne selection gesetzt. So kann formkit
            // gleiche Formulare erkennen und seinen cache verwenden (Jedes geänderte Formular braucht individuellen key für rerendering)

            sel.key = this.mj.hash([sel,this.status.productOptions]);

            // Aktuelle Auswahl (selected) setzen. Das ist kein offizielles formkit Attribut
            // Es wird hier für die formkitExtension gesetzt             
            sel.selected = this.status.form[loScheme.group];

            // Den Finalen Filter nur dann anzeigen, wenn mehr als eine Option vorhanden
            if (loScheme.filter == "final" && this.displayFinal == 'auto' && optLen < 2) continue

            // Filter nur hinzufügen, wenn er Produkte liefert
            if (this.optgroup[loScheme.group] !== false) fks.push(sel);

            if (this.stepByStep && !sel.selected) break
        }

        let productList = await this.currentFilter.toArray();

        // Wenn nur ein Produkt gefunden wurde, dieses als aktuelles Produkt setzen
        if (productList.length == 1)  {
            this.status.product = productList[0];
        } else if (this.status.form.pid) {
            // Wenn ein Produkt vorausgewählt wurde, dieses als aktuelles Produkt setzen
            let psel = productList.find((el) => el.pid == this.status.form.pid);
            if (psel)  this.status.product = psel;
        }

        if (this.status.product) {
            logger.info("product set: " + this.status.product.pid);
            updateMediaImageResolutions(this.status.media, this.status.product);

            let setupName = this.status.product.setupName;
            this.status.productOptions = [];
            this.status.optionPrices = {};
            let fksOpt = [];
            if (setupName) {
                logger.debug("fetching product setup: " + setupName); 
                let dbRes = await db.productSetup.filter(el => el.name == setupName).toArray();
                if (dbRes?.[0]?.data ) {
                    this.status.product.setup = dbRes[0].data;
                    fksOpt = this.genOptionsForm(await this.getProductConfig());
                }
            }

            // werden alle Formularelemente auf einmal angezeigt (also nicht step by step),
            // Wird ein Zwischentitel "Optionen" vor den Optionsselektoren eingefügt  
            if (!this.stepByStep && fksOpt.length > 0) {
                fks.push({ $el: 'div', attrs: { class: 'uk-text-bold' }, children: "Optionen" });
            }

            fks.push(...fksOpt);
            // fks.push(...fksMediaOpt)

            let selProdOpt = this.getSelectedProductOptions()
            .filter((option) => option.po != 0 && option.rawName !== "not_set")
            .reduce((acc, cur) => {
                const optionLabel = cur.name || '';
                acc.push({ $el: 'span', attrs: {class: 'product-option'} , children: this.t("product." + optionLabel) !== ('product.' + optionLabel)
                    ? this.t("product." + optionLabel)
                    : optionLabel});
                return acc
            }, []);
            if (this.showFinalProduct && this.status.optionsSet !== false) {
                let product_text =
                    this.status.product.label + " " +
                    this.status.product.format + " " +
                    this.status.product.surface;
                fks.push({ $el: 'div', attrs: { class: 'product-selection-text uk-container uk-background-default' }, children: [
                    { $el: 'div', attrs: { class: 'final-product'}, children: [
                        product_text,
                        { $cmp: 'ProductInfoButton', props: {
                            pid: this.status.product.pid
                        } }
                    ] },
                    { $el: 'div', children: selProdOpt },
                ] });
            }

            // immer Hash für alle Produktoptionen generieren, damit Produkte eindeutig über PID + optionsHash vergleichbar sind.
            this.genPoHash();
        }

        
        logger.debug("Produkt Form");
        logger.debug(toRaw(this.status.form));

        if (!this.slideshow) {
            return fks
        } else {
            if (!this.status.slideshow) this.status.slideshow = {};
            this.status.slideshow.length = fks.length;
            return {
                $el:'div', 
                attrs: {'uk-slideshow': `finite: true; min-height: ${layoutSlideshow.minHeight}; max-height: ${layoutSlideshow.maxHeight}`, 'itemshow': "itemshow"},
                children: [
                    {
                        $el: 'div',
                        attrs: {class: 'uk-slideshow-items'},
                        children: fks
                    },
                    {
                        $el: 'a',
                        attrs: {class: "uk-position-center-right uk-position-small uk-hidden-hover", "uk-slidenav-next":'', "uk-slideshow-item":"next"}

                    }
                ]
            }
        }

    }

    genPoHash() {
        let poValues = {};
        for (let key in this.status.form) {
            if (key.startsWith("po_")) {
                poValues[key] = this.status.form[key];
            }
        }
        this.status.productOptionsHash = this.mj.hash(poValues);
    }

    getSelectedProductOptions() {

        let productOptions = [];
        if (this.status.form) {
            for (let key in this.status.form) {
                if (key.startsWith("po_")) {
                    if (Array.isArray(this.status.form[key] && this.status.form[key].length > 0)) {
                    productOptions = productOptions.concat(this.status.form[key]);
                    } else if(this.status.form[key]){
                    productOptions.push(this.status.form[key]);
                    }
                }
           }
        }
        let detailedOptions = [];
        productOptions.forEach((po) => {
            let optDetail = this.getProductOption(po);
            if (optDetail) {
                const optionName = this.getProductOptionLabel(optDetail);
                detailedOptions.push({
                    po: optDetail.po,
                    pid: optDetail.pid,
                    groupId: optDetail.groupId,
                    name: optionName,
                    value: optDetail.value,
                    rawName: optDetail.name
                });
            }
        });
        return detailedOptions

    }

    getProductOptionLabel(optionDetail) {
        if (!optionDetail) return ''

        const rawName = optionDetail.name;
        const value = optionDetail.value;
        const name = typeof rawName === 'string' ? rawName.trim() : (rawName ?? '');
        const optionValue = typeof value === 'string' ? value.trim() : (value ?? '');

        if (name && optionValue) return `${name} ${optionValue}`
        return name || optionValue || ''
    }

    getProductOption(po) {
        let prodOpt = this.status.productOptions;
        for (let i in prodOpt) {
            if (prodOpt[i].po == po || prodOpt[i].value == po) return prodOpt[i]
            else if (Array.isArray(prodOpt[i])) {
                for (let j in prodOpt[i]) {
                    if (prodOpt[i][j].po == po || prodOpt[i][j].value == po) return prodOpt[i][j]
                }
            }
        }
        return null

    }
}

const online = useOnline();

class MercureEvents {

    constructor() {

        this.config = useConfigStore();
        this.enabled = false;
        this.hubUrl = null;

        this.refreshHubUrl();

        this.eventBindings = {};
        this.ebId = 0;
        this.hooks = {};

        const authStore = useAuthStore();
        let kid = (authStore.user?.kid)?authStore.user.kid:0;
        let sessionId = (authStore.setup?.sessionId)?authStore.setup.sessionId:0;

        const apiUrl = this.config.apiUrl;
        const apiPath = this.config.apiPath;
        this.events = [
            { topic:`${apiUrl}${apiPath}/sessions/${sessionId}/images`, type:"image", callback:"fireEvent" },
            { topic:`${apiUrl}${apiPath}/sessions/${sessionId}/media`, type:"media", callback:"fireEvent" },
            { topic:`${apiUrl}${apiPath}/sessions/${sessionId}/folders`, type:"folder", callback:"fireEvent" },
            { topic:`${apiUrl}${apiPath}/sessions/${sessionId}/state`, type:"state", callback:"fireEvent" }
        ];

        if (kid != 0) {
            this.events.push(
                { topic:`${apiUrl}${apiPath}/users/${kid}/images`, type:"image", callback:"fireEvent" },
                { topic:`${apiUrl}${apiPath}/users/${kid}/media`, type:"media", callback:"fireEvent" },
                { topic:`${apiUrl}${apiPath}/users/${kid}/folders`, type:"folder", callback:"fireEvent" },
                { topic:`${apiUrl}${apiPath}/users/${kid}/state`, type:"state", callback:"fireEvent" }
            );
        }

        this.events.forEach((evt)  => {
            this.addTopic(evt.topic);
        });

        watch(online, (connected) => {
            if (connected) {
                logger.info("reconnect mercure");
                this.listen();
            }
        });

    }

    refreshHubUrl() {
        this.enabled = false;
        this.hubUrl = null;

        try {
            if (this.config.hubUrl) {
                this.hubUrl = new URL(this.config.hubUrl);
                this.enabled = true;
            }
        } catch (error) {
            logger.error('mercureEvents: ungültige hubUrl', this.config.hubUrl, error);
        }
    }

    addTopic(topic) {
        if (!this.hubUrl) return
        if (topic[0] == "/") topic = this.config.apiUrl + topic; 
        this.hubUrl.searchParams.append('topic', topic);
    }

    addHook(name, func, options) {

        if (typeof func != "function") {
            logger.error("mercureEvent: Hook function is not a function", name, func);
            return
        }

        if (!this.hooks[name]) this.hooks[name] = {};
        this.hooks[name] = {func: func, options: options};

        logger.debug("mercureEvents add hook " + name);

        return  this.hooks[name]

    }

    removeHook(name) {
        if (this.hooks[name]) {
            delete this.hooks[name];
            logger.debug("mercureEvents remove hook " + name);
        } else {
            logger.warn("mercureEvents remove hook " + name + " not found");
        }
    }

    addListener(type,callback) {

        let that = this;

        logger.info("mercure add listener " + type);
        this.eventSource.addEventListener(type, function (e) {
            let parsedData = null;

            try {
                parsedData = JSON.parse(e.data);
                Object.keys(that.hooks).forEach((name) => {
                    let hook = that.hooks[name];
                    if (typeof hook.func == "function") {
                        hook.func(type, parsedData, e, hook.options);
                    }
                });
            } catch (error) {
                logger.error(error);
            }
            
            if (parsedData && typeof that[callback] == "function") {
            
                that[callback](type, parsedData);
            }

        }, false);

    }

    close() {
        if (this.eventSource) this.eventSource.close();
    }

    async listen() {

        if (!this.enabled || !this.hubUrl) {
            logger.warn('mercureEvents: listen übersprungen, da hubUrl nicht verfügbar ist');
            return false
        }

        this.close();

        logger.debug("start listen");
        this.eventSource = new EventSource(this.hubUrl,{withCredentials:true});
        const registeredTypes = new Set();
        this.events.forEach((evt)  => {
            if (!registeredTypes.has(evt.type)) {
                this.addListener(evt.type,evt.callback);
                registeredTypes.add(evt.type);
            }
        });


    }

    register(eventName, callback, options) {

        this.ebId ++;
        if (!this.eventBindings[eventName]) this.eventBindings[eventName] = [];
        this.eventBindings[eventName].push({id: this.ebId, callback:callback, options:options});

        logger.debug(eventName +  " registered");
        return eventName + ":" + this.ebId
    }

    unregister(eventId) {

        let [eventName, ebId] = eventId.split(":");
        this.eventBindings[eventName] = this.eventBindings[eventName].filter(function(value){ 
            return value.id != ebId;
        });
    }

    fireEvent(type, data) {

        logger.debug("mercure event", [type, data, this.eventBindings ]);
        if (this.eventBindings[type]) {
            this.eventBindings[type].forEach(evt => {
                evt["callback"](data);
            });
        }
        
    }
}

class Folder {

    constructor(folders, currentPath) {

        this.defaults();
        this.uc = 0;
        if (folders) this.folders = folders;
        else this.folders = [];
        this.currentFolder = {};
        if (currentPath) this.currentPath = currentPath;
        else this.currentPath = "/";
        this.getCurrentFolder();

    }
    
    defaults() {
      this.text = {
        1: {name:"Müll", description: "Gelöschte Dateien"}
      };
    }

    getCurrentFolder() {

      this.currentFolder = this.folders.find((fld) => fld.path == this.currentPath);
      return this.currentFolder

    }

    get(path) {

      let fld = this.folders.find((fld) => _.trim(fld.path,"/") == _.trim(path,"/"));
      if (fld && this.text[fld.oid]) {
        fld = {...fld,...this.text[fld.oid]}; 
      }

      return fld

    }

    setPath(path) {
      this.currentPath = path;
      this.getCurrentFolder();
    } 

    list() {

      // Liste der Ordner im aktuellen Pfad
      let path = this.currentPath;
      if (path[path.length-1]!="/") path = path + "/";

      let list  = this.folders.filter((fld) => {
        let fldParts = fld.path.split("/");
        let parentDir = fldParts.slice(0 , fldParts.length-1).join("/") + "/";
        if (path == parentDir && fldParts[fldParts.length-1] !="") return true
      });

      let that = this;
      list.map((fld) => {
        let text = that.text; 
        if (text && text[fld.oid]) fld.translatedName =  text[fld.oid].name;
        else fld.translatedName = fld.name;
      });

      return list

    }

    /*
    * gibt Verzeichnisse alse Verschachteltes Objekt aus
    */
    object() {

      const pathObject = {children:{}};
      this.folders.forEach(fld => {

        let path = fld.path;
        if (path[0]=="/") path = path.substring(1);
        if (path[path.length-1]=="/") path = path.substring(0,path.length-2);
        if (path == "") { 
          _.extend(pathObject, fld);
          return
        }
        let fldParts = path.split("/");
        let currentPathObject = pathObject;
        let depth = 0;
        fldParts.forEach(dir => {
          depth++;
          if (depth == fldParts.length) {
            if (currentPathObject.children[dir]) _.extend(currentPathObject.children[dir],fld); 
            else currentPathObject.children[dir] = { ...fld, children:{}};
          } else {
            if (!currentPathObject.children[dir]) {
              currentPathObject.children[dir] = { children: {}};
            }
            currentPathObject = currentPathObject.children[dir];
          }
          
        });

      });

      return pathObject
    }

    add(el) {
      this.folders.unshift(el);
      this.uc++;
    }

    load(folders) {

      
      this.folders = folders;
      this.getCurrentFolder();
      this.uc++;

    }
}

class MediaManager {

    constructor(setup) {

        this.errors = ref([]);
        this.images = ref([]);
        this.media = ref([]);
        this.folder = glob("folder", () => ref(new Folder()), true);
        this.selectedItems = ref([]);
        this.copyMove = ref([]);
        this.useRouting = false;
        this.singleSelect = false;
        this.browse = 'folder';
        this.accept = {};
        this.mediaFilter = [];
        this.excludeOids = [];
        this.excludeDirs = [];
        this.resources = ['images', 'media'];  // 'images'|'media'
        this.loaded = [];
        this.basePath = "/";
        this.editorConfig = {
            Image: {},
            kalender: {}
        };
        this.onFolderChange = null;
        this.onImagesChange = null;
        this.onMediaChange = null;
        this.onImagesReceived = null;
        this.onMediaReceived = null;
        this.externalEventDedupTtlMs = 10000;
        this.externalEventDedupMaxSize = 1000;
        this.externalEventSeenAt = new Map();

        if (setup) {
            if (typeof setup["useRouting"] != "undefined") this.useRouting = setup["useRouting"];
            if (typeof setup["browse"] != "undefined") this.browse = setup["browse"];
            if (typeof setup["accept"] != "undefined") this.accept = setup["accept"];
            if (typeof setup["singleSelect"] != "undefined") this.singleSelect = setup["singleSelect"];
            if (typeof setup["resources"] != "undefined") this.resources = setup["resources"];
            if (typeof setup["basePath"] != "undefined") this.basePath = setup["basePath"];
            if (typeof setup["excludeOids"] != "undefined") this.excludeOids = setup["excludeOids"];
            if (typeof setup["excludeDirs"] != "undefined") this.excludeDirs = setup["excludeDirs"];
        }

        this.init();

    }

    resolveEditorMediaUid(item) {
        const directUid = `${item?.uid ?? ''}`.trim();
        if (directUid) return directUid

        const jsonLdId = `${item?.['@id'] ?? ''}`.trim();
        if (!jsonLdId) return ''

        const normalized = jsonLdId.split('?')[0].replace(/\/+$/, '');
        const segments = normalized.split('/').filter(Boolean);
        return segments[segments.length - 1] || ''
    }

    init() {

        this.authStore = useAuthStore();
        const { user } = storeToRefs(this.authStore);
        this.user = user;

        watch(this.user, () => {
            this.loadAll(1);
        });

        watch(
            () => [this.user.value?.kid || 0, this.authStore.setup?.sessionId || ''],
            () => {
                this.bindMercureEvents();
            }
        );

        this.folderList = computed(() => {
            if (this.browse == "none") return []
            return this.folder.value.list()
        });

        this.mercureEvents = null;
        this.MEI = null;
        this.MEM = null;
        this.MEF = null;
        this.onMercureEventsRecreated = () => this.bindMercureEvents();
        this.releaseMercureEventsRecreated = registerPpShopEvent('pp-shop:mercure-events-recreated', this.onMercureEventsRecreated);
        this.releaseLocalUploadListener = registerLocalUploadEvent((payload) => this.updateUploadedList(payload));

        this.bindMercureEvents();

        const that = this;
        this.imageList = computed(() => {
        
            if (!Array.isArray(this.images.value)) this.images.value = [];
        
            // wenn folder browsing deaktiviert, alles außer Mülleimer zurückliefern
            if (this.browse != "folder") {
                return this.images.value.filter((el) => {
                    return el.oid != 1
                }).sort(that.sortFunction)
            }
        
            if (!this.folder.value.currentFolder) return []
            return this.images.value.filter((el) => {
                return el.oid == this.folder.value.currentFolder.oid
            }).sort(that.sortFunction)
        });
        
        // Ensure a deep watcher on images to trigger computed updates
        watch(this.images, () => {
            // No-op: this triggers reactivity for computed properties using this.images
        }, { deep: true });

        this.mediaList = computed(() => {

            if (!Array.isArray(this.media.value)) this.media.value = [];

            // wenn folder browsing deaktiviert, alles außer Mülleimer zurückliefern
            if (this.browse != "folder") {
                return this.media.value.filter((el) => {
                    return (el.oid != 1 && (this.mediaFilter.length == 0 || this.mediaFilter.indexOf(el.module) > -1))  
                }).sort(that.sortFunction)
            }

            if (!this.folder.value.currentFolder) return []
            return this.media.value.filter((el) => {
                return (el.oid == this.folder.value.currentFolder.oid && (this.mediaFilter.length == 0 || this.mediaFilter.indexOf(el.module) > -1))
            }).sort(that.sortFunction)
        });


        watch(() => this.selectedItems.value.length, (si) => {
            if (si > 0) this.copyMovePaste("reset");
        });

        if (this.useRouting) this.initRouting();

        watch(this.folder, () => {
            if (typeof this.onFolderChange == "function") this.onFolderChange(this.folder);
            logger.info("### folder change ###");
        }, { deep: true });

        watch(this.images, () => {
            if (typeof this.onImagesChange == "function") this.onImagesChange(this.images);
            logger.info("### images change ###");
        }, { deep: true });

        watch(this.media, () => {
            if (typeof this.onMediaChange == "function") this.onMediaChange(this.media);
            logger.info("### media change ###");
        }, { deep: true });

    }

    setResources(resources) {

        this.resources = resources;

    }

    setMediaFilter(mediaFilter) {

        this.mediaFilter = mediaFilter;

    }

    setBrowse(browse) {

        this.browse = browse;


    }

    initRouting() {

        try {
            this.route = useRoute();
            this.router = useRouter();
        } catch (e) {
            this.route = null;
            this.router = null;
            logger.warn(['MediaManager routing unavailable, fallback to internal path state', e]);
        }

        const applyRoutePath = (galleryPath) => {
            let pathStr = "";
            if (galleryPath) {
                pathStr = Array.isArray(galleryPath)
                    ? galleryPath.join("/")
                    : galleryPath;
            }
            this.setPath("/" + pathStr);
        };

        applyRoutePath(this.route?.params?.gallerypath);

        watch(
            () => this.route?.params?.gallerypath,
            galleryPath => {
                applyRoutePath(galleryPath);
            }
        );

        // watch(this.route, () => {
        //   this.selectNone()
        // })

    }

    loadAll(update) {

        // Wenn update = 1 werden nur resourcen geladen die bereits abgefragt wurden

        if ((!update || this.loaded["images"]) && (this.resources.indexOf("image") > -1 || this.resources.indexOf("images") > -1)) this.loadImages();
        if ((!update || this.loaded["media"]) && this.resources.indexOf("media") > -1) this.loadMedia();
        if ((!update || this.loaded["folders"]) && this.browse == "folder") this.loadFolders();

    }

    async loadImages() {

        let apiEndpoint = null;
        let queries = [];

        if (this.user?.value?.kid) {
            apiEndpoint = "users/" + this.user.value.kid + "/images";
            if (this.excludeOids.length > 0) queries.push({"exclude_oid": this.excludeOids});
            if (this.excludeDirs.length > 0) queries.push({"exclude_dir": this.excludeDirs});
        } else {
            apiEndpoint = "users/0/session/" + this.authStore.getSessionId() + "/images";
        }

        try {
            let img_raw_data = await ppapiClient.get(apiEndpoint, null, null, queries);
            if (!img_raw_data) return false
            this.images.value = img_raw_data.data();
        } catch (e) {
            logger.error(["Error loading images", e]);
            this.errors.value.push(e);
        }

        this.loaded["images"] = 1;
    }

    async loadMedia() {

        let apiEndpoint = null;
        let queries = [];

        if (this.user?.value?.kid) {
            apiEndpoint = "users/" + this.user.value.kid + "/media";
            if (this.excludeOids.length > 0) queries.push({"exclude_oid": this.excludeOids});
            if (this.excludeDirs.length > 0) queries.push({"exclude_dir": this.excludeDirs});
        } else {
            apiEndpoint = "users/0/session/" + this.authStore.getSessionId() + "/media";
        }

        try {
            let obj_raw_data = await ppapiClient.get(apiEndpoint, null, null, queries);
            if (!obj_raw_data) return false
            this.media.value = obj_raw_data.data();
        } catch (e) {
            logger.error(["Error loading media", e]);
            this.errors.value.push(e);
        }

        this.loaded["media"] = 1;

    }

    /**
     * Asynchronously loads folders for the current user or session.
     * 
     * If the user is authenticated and has a `kid` value, it loads folders specific to that user.
     * Otherwise, it loads default folders for a session with `kid:0` and `session:0`.
     * 
     * @async
     * @returns {Promise<boolean>} Returns `false` if no folder data is retrieved, otherwise `undefined`.
     * @throws Will log an error and push it to the `errors` array if the folder loading fails.
     */
    async loadFolders() {

        let apiEndpoint = null;
        let queries = [];

        if (this.user?.value?.kid) {
            apiEndpoint = "users/" + this.user.value.kid + "/folders";
             if (this.excludeOids.length > 0) queries.push({"exclude_oid": this.excludeOids});
        } else {
            // Im session Modus können momentan keine Ordner angelegt werden
            // Es werden nur Default Ordner kid:0 session:0 geladen
            //apiEndpoint = "users/0/session/" + this.authStore.getSessionId() + "/folders"
            apiEndpoint = "users/0/session/0/folders";
        }

        try {
            let kid = 0;
            if (this.user?.value?.kid) kid = this.user.value.kid;
            let fld_raw_data = await ppapiClient.get(apiEndpoint, null, null, queries);
            if (!fld_raw_data) return false
            this.folder.value.load(fld_raw_data.data().sort(this.sortFunction));
        } catch (e) {
            if (this.folder?.value?.folders) this.folder.value.folders = [];
            logger.error(["Error loading folders", e]);
            this.errors.value.push(e);
        }

        this.loaded["folders"] = 1;

    }

    setPath(path) {

        this.folder.value.setPath(path);

    }

    selectItem(item) {

        if (this.singleSelect === true) this.selectNone();

        item.selected = !item.selected;

        this.selectedItems.value = [
            ...this.folder.value.list().filter((item) => { return item.selected }),
            ...this.images.value.filter((item) => { return item.selected }),
            ...this.media.value.filter((item) => { return item.selected })
        ];
    }

    selectNone() {

        this.folder.value.list().forEach((item) => { item.selected = false; });
        this.images.value.forEach((item) => { item.selected = false; });
        this.media.value.forEach((item) => { item.selected = false; });
        this.selectedItems.value = [];
    }

    selectAll() {

        const selectedFolders = this.folder.value.list().filter((item) => { item.selected = true; return true });
        const selectedImages = this.imageList.value.filter((item) => { item.selected = true; return true });
        const selectedMedia = this.mediaList.value.filter((item) => { item.selected = true; return true });
        this.selectedItems.value = [
            ...selectedFolders,
            ...selectedImages,
            ...selectedMedia
        ];
    }



    updateFolderList(elList) {
        if (!Array.isArray(elList)) elList = [elList];
        elList.forEach((el) => {
            this.updateOrAppendFolder(el);
        });
    }

    updateUploadedList(elList) {
        if (!Array.isArray(elList)) elList = [elList];
        elList.forEach((el) => this.updateUploadedItem(el));
    }

    updateUploadedItem(el) {
        const mediaType = this.getMediaType(el);

        if (mediaType == "Image") {
            this.logUploadProcessing("local upload", el);
            this.updateImageList(el);
            return
        }

        if (mediaType) {
            this.logUploadProcessing("local upload", el);
            this.updateMediaList(el);
        }
    }

    logUploadProcessing(source, el) {
        logger.info([source, this.getUploadLogIdentity(el), el]);
    }

    getUploadLogIdentity(el) {
        if (!el || typeof el != "object") return "unknown"
        const type = el["@type"] || this.getMediaType(el) || "upload";
        const id = el.id || el.jid || el.bid || el.uid || el.name || "unknown";
        return `${type}:${id}`
    }

    resolveUploadClientId(payload) {
        if (!payload || typeof payload != "object") return ""
        return payload.clientId || payload.client_id || payload.client || payload.meta?.clientId || payload.meta?.client_id || payload.meta?.client || ""
    }

    resolveUploadEventId(payload) {
        if (!payload || typeof payload != "object") return ""
        return payload._eventId
            || payload.eventId
            || ""
    }

    cleanupExternalEventDedupCache(now = Date.now()) {
        this.externalEventSeenAt.forEach((seenAt, eventId) => {
            if (now - seenAt > this.externalEventDedupTtlMs) {
                this.externalEventSeenAt.delete(eventId);
            }
        });
    }

    shouldProcessExternalUploadEvent(payload) {
        const eventId = this.resolveUploadEventId(payload);
        if (!eventId) return true

        const now = Date.now();
        this.cleanupExternalEventDedupCache(now);

        if (this.externalEventSeenAt.has(eventId)) {
            return false
        }

        // Mark before further processing to avoid duplicate handling under tight timing.
        this.externalEventSeenAt.set(eventId, now);

        if (this.externalEventSeenAt.size > this.externalEventDedupMaxSize) {
            const oldestEventId = this.externalEventSeenAt.keys().next().value;
            if (oldestEventId) this.externalEventSeenAt.delete(oldestEventId);
        }

        return true
    }

    filterExternalUploadEvents(elList) {

        if (!Array.isArray(elList)) elList = [elList];
        return elList.filter((el) => this.shouldProcessExternalUploadEvent(el))
    }

    updateExternalImageList(elList) {
        const externalItems = this.filterExternalUploadEvents(elList);
        externalItems.forEach((el) => this.logUploadProcessing("external upload", el));
        if (externalItems.length > 0) this.updateImageList(externalItems);
    }

    updateExternalMediaList(elList) {
        const externalItems = this.filterExternalUploadEvents(elList);
        externalItems.forEach((el) => this.logUploadProcessing("external media event", el));
        if (externalItems.length > 0) this.updateMediaList(externalItems);
    }

    applyLocalMove(typeList, oid) {
        const updateList = (items, ids) => {
            items.forEach((item) => {
                if (ids.includes(item.id)) item.oid = oid;
            });
        };

        updateList(this.images.value, typeList.Image || []);
        updateList(this.media.value, typeList.Media || []);
    }

    updateImageList(elList) {
        if (!Array.isArray(elList)) elList = [elList];
        elList.forEach((el) => {
            this.updateOrAppendImage(el);
        });
        if (typeof this.onImagesReceived == "function") this.onImagesReceived(elList);

    }

    updateMediaList(elList) {
        if (!Array.isArray(elList)) elList = [elList];
        elList.forEach((el) => {
            this.updateOrAppendMedia(el);
        });

    }

    updateOrAppendImage(el) {

        let updated = false;
        for (let i in this.images.value) {
            if (this.images.value[i].id == el.id) {
                this.images.value[i] = el;
                updated = true;
                return
            }
        }
        if (!updated) this.images.value.unshift(el);
    }

    updateOrAppendMedia(el) {

        let updated = false;
        for (let i in this.media.value) {
            if (this.media.value[i].id == el.id) {
                this.media.value[i] = el;
                updated = true;
                return
            }
        }
        if (!updated) this.media.value.unshift(el);
    }

    updateOrAppendFolder(el) {

        let updated = false;
        for (let i in this.folder.value.folders) {
            if (this.folder.value.folders[i].id == el.id) {
                this.folder.value.folders[i] = el;
                updated = true;
                return
            }
        }
        if (!updated) this.folder.value.folders.unshift(el);
    }

    async createFolder(name) {

        let newPath = this.folder.value.currentPath;
        if (newPath.substr(-1) != "/") newPath += "/";
        newPath += name;

        let new_fld_raw_data = await ppapiClient.post("folders", { name: name, kid: this.user.value.kid, path: newPath });
        logger.debug(new_fld_raw_data);

    }

    /**
     * Navigates into a directory or updates the path based on the provided item.
     * 
     * @param {Object} item - The item representing the directory to enter.
     * @param {string} item.path - The path of the directory. It is always the full path starting with "/". For example: "/folder1/subfolder2".
     * @param {number} index - The index of the item in the list.
     */
    enterDir(item, index) {

        this.selectNone();

        if (this.useRouting && this.router?.push) {

            let basePath = this.basePath;

            let urlpath = item.path.substring(1).replaceAll("#", "%23");
            if (basePath[basePath.length - 1] == "/") basePath = basePath.substring(0, basePath.length - 1);

            this.router.push(basePath + "/" + urlpath);

            

        } else {
            this.setPath(item.path);
        }
    }



    copyMovePaste(task) {

        if (task == "move" || task == "copy") {

            this.copyMove.value.task = task;
            this.copyMove.value.items = [...this.selectedItems.value];
            this.copyMove.value.sourceOid = this.folder.value.currentFolder?.oid;
            let idList = [];
            this.selectedItems.value.reduce((acc, val) => idList.push(val["@type"] + ":" + val.id), idList);
            this.copyMove.value.idList = idList;

            this.selectNone();
        }

        if (task == "paste") {
            if (!this.copyMove.value.items?.length || this.copyMove.value.sourceOid === this.folder.value.currentFolder?.oid) return

            if (this.copyMove.value.task == "move") this.moveItems(this.folder.value.currentFolder, this.copyMove.value.items);
            else if (this.copyMove.value.task == "copy") this.copyItems(this.folder.value.currentFolder, this.copyMove.value.items);
            this.copyMove.value = {};
        }

        if (task == "reset") {
            this.copyMove.value = {};
        }

    }

    copyItems(target, list) {


        if (!list) list = this.selectedItems.value;

        let oid = false;
        if (typeof target == "object") oid = target.oid;
        else oid = target;

        const idList = this.copyMove.value.items.map((item) => { return item.id });

        let post = { '@copy': { id: idList, set: { oid: oid } } };
        ppapiClient.post("images/copy", post);
        this.selectNone();

    }

    moveItems(target, list) {

        if (!list) list = this.selectedItems.value;

        let oid = false;
        if (typeof target == "object") oid = target.oid;
        else oid = target;

        // execute API updates by type
        const typeList = this.listIdsByType(list);
        const alertStore = useAlertStore();
        const clientId = this.authStore.getClientId();
        const clientMeta = { clientId };
        let nameConflicts = [];

        for(let type in typeList) {
            switch(type) {
                case "Image" :
                ppapiClient.put("images", { ...clientMeta, '@update': { idList: typeList["Image"], set: { oid: oid } } }).then(() => {
                        this.applyLocalMove(typeList, oid);
                    }, (error) => {
                        alertStore.error(["Error moving items",error]);
                    });
                break;
                case "Media" :
                ppapiClient.put("media", { ...clientMeta, '@update': { idList: typeList["Media"], set: { oid: oid } } }).then(() => {
                        this.applyLocalMove(typeList, oid);
                    }, (error) => {
                        alertStore.error(["Error moving items",error]);
                    });
                break;
                case "Folder" :
                    // moving folders is done by updating path. So get path of target folder oid
                    var targetFolder = this.folder.value.folders.find((item) => { return item.oid == oid });
                    if (!targetFolder)  {
                        alertStore.error("Path for moving folders not found");
                        break;
                    }
                    // check if target folder has folders with same basename (Does not apply for trash)
                    if (targetFolder.path != "/trash") {
                        nameConflicts = [];
                        typeList["Folder"].forEach((folderItem) => {
                        let basename = folderItem.path.split('/').reverse()[0];
                            let nc = this.folder.value.folders.find((item) => item.path == targetFolder.path + "/" + basename);
                            if (nc) nameConflicts.push(basename);
                        });

                        if (nameConflicts.length > 0) {
                            alertStore.error(["Target folder already has subfolders with the same name: " + nameConflicts.join(", ")]);
                            break;
                        }
                    }

                    ppapiClient.put("folders", { '@update': { idList: typeList["Folder"], toPath: targetFolder.path } }).then({}, (error) => {
                        alertStore.error(["Error moving items",error]);
                    });
                    
                break;  
            }
        }

        this.selectNone();

    }

    editItem(item) {

        const configStore = useConfigStore();

        if (!item) item = this.selectedItems.value[0];

        let mtype = this.getMediaType(item);
        let query = "";

        switch (mtype) {

            case "calendar":

                const mediaUid = this.resolveEditorMediaUid(item);
                if (!mediaUid) {
                    logger.warn('[MediaManager] calendar editor requires media uid but none was found', item);
                    return
                }

                query = "?media=" + encodeURIComponent(mediaUid) + "&dbkey=" + this.authStore.setup?.dbKey;
                if (!this.authStore.user) query += "&session=" + this.authStore.setup?.sessionId;
                document.location.href = configStore.calendarEditPath + query;

                break;
            default:

                alert(`Dokument vom Typ ${mtype} kann nicht bearbeitet werden`);

        }
        // TODO open specific Editor

    }

    listIdsByType(list) {

        const typeList = {};

        list.forEach((item) => {
            let type = "unkown";
            if (item['@type']) type = item['@type'];
            if (!typeList[type]) typeList[type] = []; 
            if (item['@type'] == "Folder") typeList[type].push({id: item.id, path: item.path});
            else typeList[type].push(item.id);   
        });

        return typeList

    }


    sortFunction(a, b) {

        if (a.date > b.date) return -1
        else return 1
    }

    close() {

        if (this.releaseMercureEventsRecreated) {
            this.releaseMercureEventsRecreated();
            this.releaseMercureEventsRecreated = null;
        }
        if (this.releaseLocalUploadListener) {
            this.releaseLocalUploadListener();
            this.releaseLocalUploadListener = null;
        }
        this.unbindMercureEvents();

    }

    bindMercureEvents() {

        const currentMercureEvents = glob("mercureEvents");
        if (!currentMercureEvents) return

        if (this.mercureEvents === currentMercureEvents && this.MEI && this.MEM && this.MEF) {
            return
        }

        this.unbindMercureEvents();
        this.mercureEvents = currentMercureEvents;

        const that = this;
        this.MEI = this.mercureEvents.register("image", (el) => that.updateExternalImageList(el));
        this.MEM = this.mercureEvents.register("media", (el) => that.updateExternalMediaList(el));
        this.MEF = this.mercureEvents.register("folder", (el) => that.updateFolderList(el));

    }

    unbindMercureEvents() {

        if (this.mercureEvents) {
            if (this.MEI) this.mercureEvents.unregister(this.MEI);
            if (this.MEM) this.mercureEvents.unregister(this.MEM);
            if (this.MEF) this.mercureEvents.unregister(this.MEF);
        }

        this.MEI = null;
        this.MEM = null;
        this.MEF = null;
    }

    getMediaType(item) {

        if (typeof item != "object") return false
        if (item["@type"] == "Image") return "Image"
        if (item["@type"] == "Media") {
            return item["module"]
        }
        if (item.bid) return "Image"
        if (item.jid || item.module) return item.module || "Media"
    }
}

class OrderManager {

  constructor(setup) {

    const authStore = useAuthStore();
    const configStore = useConfigStore();
    const { user }  = storeToRefs(authStore);
    const { accountingYears }  = storeToRefs(configStore);
    this.user = user;
    this.accountingYears = accountingYears;

    this.accounting = ref({});
    this.purchases = ref({});

  }

  async load(dateFilter) {
    await this.loadPurchases(dateFilter);
    await this.loadAccounting(dateFilter);
  }

  buildDateFilterQueries(dateFilter) {

    if (!dateFilter) return []

    // Backward compatible: previously the manager accepted a plain date string.
    if (typeof dateFilter === 'string') {
        return [{ date: dateFilter }]
    }

    // Backward compatible: older callers passed year as number.
    if (typeof dateFilter === 'number' && Number.isInteger(dateFilter)) {
      return [{ 'dateFilter[year]': dateFilter }]
    }

    if (typeof dateFilter !== 'object') return []

    const queries = [];

    function appendFilter(key, value) {
        if (typeof value === 'undefined' || value === null || value === '') return
        queries.push({[`dateFilter[${key}]`]: value});
    }

    appendFilter('year', dateFilter.year);
    appendFilter('lastDays', dateFilter.lastDays);
    appendFilter('from', dateFilter.from);
    appendFilter('to', dateFilter.to);

    return queries
  }

  buildDateFilterWithSortQueries(dateFilter) {
    return [...this.buildDateFilterQueries(dateFilter), {"order[id]":"desc"}]
  }

  async loadPurchases(dateFilter) {
    
    if (!this.user?.value) return false

    const dateFilterQueries = this.buildDateFilterWithSortQueries(dateFilter);

    let purchases = await ppapiClient.get(`users/${this.user.value.kid}/purchases`,null,null,dateFilterQueries);
    purchases.aid = [];
    purchases.ref = [];
    purchases.data().forEach((elem) => {
        purchases.aid[elem.aid] = elem;
        if (!purchases.ref[elem.reference]) purchases.ref[elem.reference] = [];
        purchases.ref[elem.reference].push(elem);
    });
    this.purchases.value = purchases;
  }

  async loadAccounting(dateFilter) {

    if (!this.user?.value) return false

    const dateFilterQueries = this.buildDateFilterWithSortQueries(dateFilter);

    this.accounting.value = await ppapiClient.get(
      `users/${this.user.value.kid}/accounting`,
      null,
      null,
      dateFilterQueries
    );

    let list = [];
    const purchases = this.purchases.value;

    this.accounting.value.data().forEach((elem) => {
        elem.purchases = [];
        if (elem.reference && purchases.ref && purchases.ref[elem.reference]) [
            elem.purchases = purchases.ref[elem.reference]
        ];
        list.push(elem);
    });
    this.accounting.value.list = list;

  }

  async getYears() {

    if (!this.user?.value) return false

    // Die Jahresliste wird nur einmal pro session geladen
    // und in der config gespeichert.
    if (this.accountingYears.value.length > 0) return this.accountingYears.value
    
    const years = await ppapiClient.get(`users/${this.user.value.kid}/accountingyears`);

    // Aktuelle Jahr in die Liste einfügen, falls nicht vorhanden
    if (!years.find((y) => y.year == new Date().getFullYear())) {
        years.push({ year: new Date().getFullYear(), count:0 });
    }
    this.accountingYears.value = years;

    return years

  }
}


// const selectedYear = ref(new Date().getFullYear());
// const years = ref([]);

// // TODO: Das geht hier nicht. Am besten die Jahresliste in OrderManager ermitteln
// onBeforeMount(() => {
//     if (accounting.value.list?.length > 0) {
//         years.value = [...new Set(accounting.value.list.map(item => new Date(item.date).getFullYear()))];
//         if (!years.value.includes(selectedYear.value)) {
//             selectedYear.value = years.value[0];
//         }
//     }
// });

// const filteredAccountingList = computed(() => {
//     if (!accounting.value.list) return []
//     return accounting.value.list.filter(item => new Date(item.date).getFullYear() === selectedYear.value);
// });

/**
 * Class representing condition that have to be fullfilled by selected media.
 */
class CheckMediaCondition {

    /**
     * Create a CheckMediaCondition.
     * @param {Object} setup - The setup object.
     * @param {Object} setup.product - The product object.
     */
    constructor(setup) {
        this.product = {};
        if (setup?.product) this.product = setup.product;
    }

    /**
     * Match the product with the given file.
     * @param {Object} file - The file object to match.
     * @returns {boolean} - Returns true if the product matches the file, otherwise false.
     */
    matchProduct(file) {
        let match = false;

        for (var i in mediaConditionFilter) {
            let mc = this.matchProductCondition(file, mediaConditionFilter[i]);
            if (mc === true) {
                match = true;
                break;
            }
        }

        return match;
    }

    /**
     * Match the product condition with the given file.
     * @param {Object} file - The file object to match.
     * @param {Object} condition - The condition object to match.
     * @returns {boolean|null} - Returns true if the condition matches the file, otherwise false. Returns null if the file or condition is invalid.
     */
    matchProductCondition(file, condition) {
        if (!file || !condition || !Array.isArray(condition.productMatch)) return null;
        if (!condition.contentType || file["@type"].toLowerCase() != condition.contentType) return null;

        this.mismatch = null;
        let match = false;

        condition.productMatch.forEach((cm) => {
            if (!isObject(cm)) return;
            let mp = true;

            for (var prop in cm) {
                mp = this.matchProperty(file, prop, cm[prop]);
                if (mp === false) {
                    this.mismatch = { pid: this.product.pid, property: prop, value: file[prop], condition: this.requiredCondition, required: this.requiredValue };
                    logger.info({ missmatch: this.mismatch });
                    break;
                }
            }
            match = mp;
        });

        if (match && file._mismatch) file._mismatch = null;
        if (!match) file._mismatch = { ...this.mismatch };

        return match;
    }

    /**
     * Match the property of the file with the given match property.
     * @param {Object} file - The file object to match.
     * @param {string} prop - The property name to match.
     * @param {string|Object} matchProperty - The match property value or condition.
     * @returns {boolean} - Returns true if the property matches, otherwise false.
     */
    matchProperty(file, prop, matchProperty) {
        if (isString(matchProperty)) {
            let matchPropArray = matchProperty.split(".");
            let propStr = matchPropArray.shift();
            let productProperty = ""; 

            // propString beginnend mit ":" ist konstanter Wert, sonst produkt Eigenschaftswert
            if (propStr[0] == ":") productProperty = propStr.substring(1);
            else productProperty = this.product[propStr];
            
            matchPropArray.forEach(el => {
                if (!productProperty) return;
                productProperty = productProperty[el];
            });

            this.requiredValue = productProperty;
            this.requiredCondition = "equal";
            if (typeof (productProperty) == "undefined" || !productProperty|| file[prop] == productProperty || (Array.isArray(productProperty) && productProperty.includes(file[prop]))) {
                return true;
            }
        } else {
            let match_function = Object.keys(matchProperty)[0];
            let param = matchProperty[match_function];
            this.requiredCondition = match_function;

            let param_values = [];
            let par_str = "";
            switch (match_function) {
                case "between":
                    if (!Array.isArray(param)) return false;
                    param_values = [this.product[param[0]], this.product[param[1]]];
                    this.requiredValue = param_values;
                    if (file[prop] >= Math.min(...param_values) && file[prop] <= Math.max(...param_values)) {
                        return true;
                    }
                    break;
                case "lower":
                    this.requiredValue = this.product[param];
                    if (file[prop] < this.product[param]) return true;
                    break;
                case "greater":
                    this.requiredValue = this.product[param];
                    if (file[prop] > this.product[param]) return true;
                    break;
                case "or":                   
                    for (let i in param) {
                       let p = param[i]; 
                       if (p[0] == ":") par_str = p.substring("1");
                       else par_str = this.product[p];
                       if (file[prop] == par_str) return true
                    }

            }
        }
 
        return false;
    }
}

class Queue {
    constructor() { this._items = []; }
    enqueue(item) { this._items.push(item); }
    dequeue()     { return this._items.shift(); }
    get size()    { return this._items.length; }
  }
  
/**
 * AutoQueue class extends the Queue class to handle asynchronous actions in a queue.
 * It ensures that only one action is processed at a time.
 * 
 * @class AutoQueue
 * @extends Queue
 */
class AutoQueue extends Queue {
  /**
   * Creates an instance of AutoQueue.
   * Initializes the _pendingPromise flag to false.
   * 
   * @constructor
   */
  constructor() {
    super();
    this._pendingPromise = false;
  }

  /**
   * Enqueues an action to be processed.
   * 
   * @param {Function} action - The action to be enqueued. It should return a promise.
   * @returns {Promise} - A promise that resolves or rejects based on the action's outcome.
   */
  enqueue(action) {
    return new Promise((resolve, reject) => {
      super.enqueue({ action, resolve, reject });
      this.dequeue();
    });
  }

  /**
   * Dequeues and processes the next action in the queue.
   * If an action is already being processed, it returns false.
   * 
   * @returns {Promise<boolean>} - A promise that resolves to true if an action was processed, otherwise false.
   */
  async dequeue() {
    if (this._pendingPromise) return false;

    let item = super.dequeue();

    if (!item) return false;

    try {
      this._pendingPromise = true;

      let payload = await item.action(this);

      this._pendingPromise = false;
      item.resolve(payload);
    } catch (e) {
      this._pendingPromise = false;
      item.reject(e);
    } finally {
      this.dequeue();
    }

    return true;
  }
}

export { AutoQueue as A, CheckMediaCondition as C, Folder as F, MercureEvents as M, OrderManager as O, ProductSelector as P, MediaManager as a };
